/**
 * Defensive normalisation of product location/metafield fields for the grid.
 *
 * A sync bug (pre-5.71.1) wrote raw GraphQL metafield objects ({ value: 'B-12' })
 * into the Firestore product cache for overflow1/overflow2. Rendering such an
 * object crashes React (error #31 — object with keys {value} as a child), and the
 * grid also persists products to localStorage, so polluted objects survive a
 * backend fix until the browser state is normalised. Every product entering grid
 * state goes through this so no {value} object can ever reach a render.
 */

const TEXT_FIELDS = ['binLocation', 'location', 'overflow1', 'overflow2', 'packingNotes'];
const DIMENSION_FIELDS = ['length', 'width', 'height'];

/** Coerce a metafield-ish value ({ value } object, string, number) to a trimmed string or null. */
export function metafieldText(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object') {
    const inner = raw.value;
    if (inner === null || inner === undefined || typeof inner === 'object') return null;
    const s = String(inner).trim();
    return s || null;
  }
  const s = String(raw).trim();
  return s || null;
}

/** Coerce a dimension-ish value ({ value } object, string, number) to a finite number or null. */
export function dimensionNumber(raw) {
  const base = (raw && typeof raw === 'object') ? raw.value : raw;
  const n = parseFloat(base);
  return Number.isFinite(n) ? n : null;
}

/**
 * Return a copy of the product with location/metafield fields guaranteed to be
 * render-safe scalars. Fields that are absent stay absent; `storeMetafields`
 * per-store entries are normalised too (they render in cross-store tooltips).
 */
export function normalizeProductLocationFields(product) {
  if (!product || typeof product !== 'object') return product;

  const fixed = { ...product };

  TEXT_FIELDS.forEach((key) => {
    if (fixed[key] !== undefined && typeof fixed[key] === 'object' && fixed[key] !== null) {
      fixed[key] = metafieldText(fixed[key]);
    }
  });

  DIMENSION_FIELDS.forEach((key) => {
    if (fixed[key] !== undefined && typeof fixed[key] === 'object' && fixed[key] !== null) {
      fixed[key] = dimensionNumber(fixed[key]);
    }
  });

  if (fixed.storeMetafields && typeof fixed.storeMetafields === 'object') {
    const healed = {};
    Object.entries(fixed.storeMetafields).forEach(([storeName, data]) => {
      if (data && typeof data === 'object') {
        healed[storeName] = {
          ...data,
          binLocation: metafieldText(data.binLocation),
          overflow1: metafieldText(data.overflow1),
          overflow2: metafieldText(data.overflow2)
        };
      } else {
        healed[storeName] = data;
      }
    });
    fixed.storeMetafields = healed;
  }

  return fixed;
}
